import { db } from './firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { User, Organization, FREEMIUM_LIMITS } from '../types';

export interface SubscriptionUsage {
  isOrganization: boolean;
  tier: 'free' | 'premium';
  status: 'active' | 'defaulted' | 'suspended';
  limit: number;
  currentCount: number;
  remaining: number;
  isLimitReached: boolean;
  orgName?: string;
}

export const subscriptionService = {
  /**
   * Calculate current QR code usage and limits for either an individual user or organization.
   */
  getUsageAndLimits: async (user: User | null): Promise<SubscriptionUsage> => {
    if (!user) {
      return {
        isOrganization: false,
        tier: 'free',
        status: 'active',
        limit: FREEMIUM_LIMITS.INDIVIDUAL,
        currentCount: 0,
        remaining: FREEMIUM_LIMITS.INDIVIDUAL,
        isLimitReached: false,
      };
    }

    const isOrganization = Boolean(user.organizationId);

    // Organization Flow
    if (isOrganization && user.organizationId) {
      let orgTier: 'free' | 'premium' = 'free';
      let orgStatus: 'active' | 'defaulted' | 'suspended' = 'active';
      let orgName = 'Organization';

      try {
        const orgDoc = await getDoc(doc(db, 'organizations', user.organizationId));
        if (orgDoc.exists()) {
          const orgData = orgDoc.data() as Organization;
          orgTier = orgData.subscriptionTier || 'free';
          orgStatus = orgData.subscriptionStatus || 'active';
          orgName = orgData.companyName || 'Organization';
        }
      } catch (err) {
        console.warn('Failed to load organization subscription from Firestore:', err);
      }

      // If Super Admin or Master Admin has explicitly configured premium
      const isPremium = orgTier === 'premium' && orgStatus === 'active';
      const limit = isPremium ? Infinity : FREEMIUM_LIMITS.ORGANIZATION;

      // Count registered items for this organization
      const countedAssetIds = new Set<string>();

      try {
        const itemsQ = query(
          collection(db, 'verisure_items'),
          where('organizationId', '==', user.organizationId)
        );
        const snap = await getDocs(itemsQ);
        snap.docs.forEach((d) => {
          const data = d.data();
          const key = data.assetId || d.id;
          if (key) countedAssetIds.add(key);
        });
      } catch (err) {
        console.warn('Error querying organization items:', err);
      }

      // Also count blank QR codes created by/for this organization
      try {
        const blankQ = query(
          collection(db, 'verisure_blank_qrs'),
          where('createdBy', '==', user.id)
        );
        const snap = await getDocs(blankQ);
        snap.docs.forEach((d) => {
          const data = d.data();
          const key = data.tagId || d.id;
          if (key) countedAssetIds.add(key);
        });
      } catch (err) {
        console.warn('Error querying blank QRs for org count:', err);
      }

      // Check local caches
      try {
        const localItems = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
        localItems.forEach((it: any) => {
          if (it.organizationId === user.organizationId) {
            const key = it.assetId || it.id;
            if (key) countedAssetIds.add(key);
          }
        });
      } catch {}

      const currentCount = countedAssetIds.size;
      const isLimitReached = !isPremium && currentCount >= limit;
      const remaining = isPremium ? Infinity : Math.max(0, limit - currentCount);

      return {
        isOrganization: true,
        tier: orgTier,
        status: orgStatus,
        limit,
        currentCount,
        remaining,
        isLimitReached,
        orgName,
      };
    }

    // Individual User Flow
    const userTier: 'free' | 'premium' = user.subscriptionTier || 'free';
    const userStatus: 'active' | 'defaulted' | 'suspended' = user.subscriptionStatus || 'active';
    const isPremium = userTier === 'premium' && userStatus === 'active';
    const limit = isPremium ? Infinity : FREEMIUM_LIMITS.INDIVIDUAL;

    const countedAssetIds = new Set<string>();

    try {
      // Find items created by or owned by this individual
      if (user.id) {
        const qByCreator = query(
          collection(db, 'verisure_items'),
          where('createdBy', '==', user.id)
        );
        const snapCreator = await getDocs(qByCreator);
        snapCreator.docs.forEach((d) => {
          const data = d.data();
          const key = data.assetId || d.id;
          if (key) countedAssetIds.add(key);
        });
      }

      if (user.email) {
        const qByEmail = query(
          collection(db, 'verisure_items'),
          where('ownerEmail', '==', user.email)
        );
        const snapEmail = await getDocs(qByEmail);
        snapEmail.docs.forEach((d) => {
          const data = d.data();
          const key = data.assetId || d.id;
          if (key) countedAssetIds.add(key);
        });
      }
    } catch (err) {
      console.warn('Error querying user items in Firestore:', err);
    }

    // Combine local storage cache
    try {
      const localItems = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
      localItems.forEach((it: any) => {
        if (
          it.createdBy === user.id ||
          it.ownerEmail === user.email ||
          it.ownerName === user.name
        ) {
          const key = it.assetId || it.id;
          if (key) countedAssetIds.add(key);
        }
      });
    } catch {}

    const currentCount = countedAssetIds.size;
    const isLimitReached = !isPremium && currentCount >= limit;
    const remaining = isPremium ? Infinity : Math.max(0, limit - currentCount);

    return {
      isOrganization: false,
      tier: userTier,
      status: userStatus,
      limit,
      currentCount,
      remaining,
      isLimitReached,
    };
  },

  /**
   * Check if user/organization can register additional QR code(s).
   */
  canRegisterQr: async (
    user: User | null,
    additionalCount: number = 1
  ): Promise<{ allowed: boolean; usage: SubscriptionUsage; message?: string }> => {
    const usage = await subscriptionService.getUsageAndLimits(user);

    if (usage.tier === 'premium' && usage.status === 'active') {
      return { allowed: true, usage };
    }

    if (usage.currentCount + additionalCount > usage.limit) {
      const entity = usage.isOrganization ? 'organization' : 'individual';
      const limit = usage.limit;
      return {
        allowed: false,
        usage,
        message: `Freemium limit reached! ${
          usage.isOrganization ? 'Organizations' : 'Individuals'
        } on the Free Plan are limited to ${limit} QR codes. You currently have ${
          usage.currentCount
        }. Please upgrade to Premium for unlimited QR codes.`,
      };
    }

    return { allowed: true, usage };
  },

  /**
   * Upgrade an individual user to Premium tier.
   */
  upgradeUserSubscription: async (userId: string, tier: 'premium' = 'premium'): Promise<void> => {
    await updateDoc(doc(db, 'users', userId), {
      subscriptionTier: tier,
      subscriptionStatus: 'active',
      subscriptionExpiry: null,
    });

    // Update demo user if running in demo sandbox
    const demo = localStorage.getItem('demo_auth_user');
    if (demo) {
      try {
        const parsed = JSON.parse(demo);
        if (parsed.id === userId) {
          parsed.subscriptionTier = tier;
          parsed.subscriptionStatus = 'active';
          localStorage.setItem('demo_auth_user', JSON.stringify(parsed));
        }
      } catch {}
    }
  },

  /**
   * Upgrade an organization to Premium tier.
   */
  upgradeOrgSubscription: async (orgId: string, tier: 'premium' = 'premium'): Promise<void> => {
    await updateDoc(doc(db, 'organizations', orgId), {
      subscriptionTier: tier,
      subscriptionStatus: 'active',
      subscriptionExpiry: null,
    });
  },
};
